import {ViewController} from "data/types/structure";
import type {ILeagueCreatePayload} from "data/types/api";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable} from "mobx";
import React from "react";
import {LeaguePrivacy, ModalType, RequestState} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {ILeaguesStore} from "data/stores/leagues/leagues.store";
import {AxiosError} from "axios";
import type {ILeague} from "data/types/entities";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import {IRound} from "data/providers/json/json.provider";
import {Empty} from "data/types/generics";

interface ILeagueUpdateForm extends HTMLFormElement {
	leagueName: HTMLInputElement;
	privacy: HTMLInputElement;
	startId: HTMLInputElement;
}

export interface IFormLeagueUpdateController extends ViewController {
	handleForm: (event: React.SyntheticEvent<ILeagueUpdateForm>) => void;
	updateLeague: (params: ILeagueCreatePayload) => void;
	resetFormErrors: () => void;
	showRemoveModal: () => void;

	error?: string;
	get isLoading(): boolean;
	get updatedLeague(): boolean;
	get rounds(): IRound[];
	get league(): Empty<ILeague>;
	get leagueStarted(): boolean;
}

@injectable()
export class FormLeagueUpdateController implements IFormLeagueUpdateController {
	@observable _requestState: RequestState = RequestState.IDLE;
	@observable _error?: string = undefined;
	@observable _isUpdatedLeague: boolean = false;

	constructor(
		@inject(Bindings.LeaguesStore) private _leaguesStore: ILeaguesStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.LeaguesStore) private _leagueStore: ILeaguesStore,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore
	) {
		makeAutoObservable(this);
	}

	get error() {
		return this._error;
	}

	get isLoading() {
		return this._requestState === RequestState.PENDING;
	}

	get updatedLeague() {
		return this._isUpdatedLeague;
	}

	get rounds() {
		return this._roundsStore.scheduleRounds;
	}

	get league(): Empty<ILeague> {
		return this._leaguesStore.selectedLeague;
	}

	get leagueStarted(): boolean {
		return this.league?.status !== "scheduled";
	}

	@action resetFormErrors = () => {
		this._error = undefined;
		this._requestState = RequestState.IDLE;
		this._isUpdatedLeague = true;
	};

	@action showRemoveModal = () => {
		this._modalsStore.showModal(ModalType.LEAGUE_REMOVE);
	};

	@action private onFormError = (error: AxiosError<{errors: {message: string}[]}>) => {
		this._error = error.response?.data?.errors[0]?.message || error.message;
		this._requestState = RequestState.ERROR;
	};

	@action updateLeague({name, privacy, startId}: ILeagueCreatePayload): void {
		if (!this.league) {
			return;
		}
		this._requestState = RequestState.PENDING;
		this._leagueStore
			.updateLeagueById(this.league.id, {
				name,
				privacy: privacy as LeaguePrivacy,
				startId: parseInt(startId, 0),
			})
			.catch(this.onFormError)
			.finally(this.onFinally);
	}

	@action handleForm = (event: React.SyntheticEvent<ILeagueUpdateForm>) => {
		event.preventDefault();
		const {leagueName, privacy, startId} = event.currentTarget;

		void this.updateLeague({
			name: leagueName.value,
			privacy: privacy.value,
			startId: startId.value,
		});
	};

	dispose(): void {
		return;
	}

	init(param: void): void {
		this.fetchRounds();
	}

	private fetchRounds(): void {
		void this._roundsStore.fetchRounds();
	}

	private onFinally = () => {
		this._requestState = RequestState.SUCCESS;
		this._isUpdatedLeague = false;
	};
}
